import type { RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import noop from 'lib/function/noop'

interface UseTripPlannerMenuDrawerArgs {
  openerRef: RefObject<HTMLElement | null>
  closeSignal?: number
  onDrawerOpenChange?: (isOpen: boolean) => void
}

interface TripPlannerMenuDrawerState {
  isMenuOpen: boolean
  openMenu: () => void
  closeMenu: () => void
  closeMenuOnNavigate: () => void
  onMenuAfterClose: () => void
}

export function useTripPlannerMenuDrawer({
  openerRef,
  closeSignal,
  onDrawerOpenChange = noop,
}: UseTripPlannerMenuDrawerArgs): TripPlannerMenuDrawerState {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // Stays true through the close animation, so a reopen cannot cancel the close and skip onMenuAfterClose.
  const isMenuShownRef = useRef(false)
  // The drawer's modal doesn't restore focus, and a navigation hands focus to the page it opens instead.
  const returnsFocusRef = useRef(true)
  const previousCloseSignalRef = useRef(closeSignal)

  const openMenu = useCallback(() => {
    if (isMenuShownRef.current) return

    isMenuShownRef.current = true
    returnsFocusRef.current = true
    setIsMenuOpen(true)
    onDrawerOpenChange(true)
  }, [onDrawerOpenChange])

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
  }, [])

  const closeMenuOnNavigate = useCallback(() => {
    returnsFocusRef.current = false
    setIsMenuOpen(false)
  }, [])

  const onMenuAfterClose = useCallback(() => {
    isMenuShownRef.current = false
    if (returnsFocusRef.current) {
      openerRef.current?.focus()
    }
    onDrawerOpenChange(false)
  }, [onDrawerOpenChange, openerRef])

  useEffect(() => {
    if (closeSignal === undefined || closeSignal === previousCloseSignalRef.current) return

    previousCloseSignalRef.current = closeSignal
    closeMenu()
  }, [closeMenu, closeSignal])

  return {
    isMenuOpen,
    openMenu,
    closeMenu,
    closeMenuOnNavigate,
    onMenuAfterClose,
  }
}
